import Avo from 'analytics/Avo';
import type { Preferences } from 'api/types/preferences.types';
import type { ServiceLine } from 'api/types/service.types';
import OrderInstructionForm from 'packages/shared/newOrder/modules/OrderInstructions/OrderInstructionForm';
import { selectCurrentOrder, selectPreference, selectServiceLineCollectionMethods } from 'pages/newOrder/newOrder.selectors';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { RootState } from 'redux-stores/reducers/rootReducer';
import { styled } from 'styled-components';

const StyledOrderInstructionForm = styled(OrderInstructionForm).attrs(({ theme }) => ({
  categoryNameStyle: {
    fontWeight: 500,
    fontSize: '16px',
    color: theme.colors.steelBlue,
    marginLeft: -3,
    marginTop: 5,
  },
}))``;

interface DriverInstructionsProps {
  serviceLine?: ServiceLine;
  formRef: React.RefObject<HTMLFormElement>;
  showCollection?: boolean;
}

const DriverInstructions = ({ serviceLine, formRef, showCollection }: DriverInstructionsProps) => {
  const { isDropoffAtDoor: defaultDropOffAtDoor, isPickupAtDoor } = useSelector(selectCurrentOrder);
  const preferences = useSelector((state: RootState) => selectPreference(state, undefined, serviceLine));
  const serviceLineDropoffMethods = useSelector(selectServiceLineCollectionMethods);
  const { t } = useTranslation();
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

  const isDropoffAtDoor = useMemo(
    () => (serviceLine ? serviceLineDropoffMethods[serviceLine]?.isDropoffAtDoor : defaultDropOffAtDoor),
    [defaultDropOffAtDoor, serviceLine, serviceLineDropoffMethods]
  );

  const pickUpOptions = useMemo(
    () => (preferences ? preferences.driverPickupInstructions[isPickupAtDoor ? 'atDoor' : 'inPerson'] : undefined),
    [isPickupAtDoor, preferences]
  );

  const dropOffOptions = useMemo(
    () => (preferences ? preferences.driverDropoffInstructions[isDropoffAtDoor ? 'atDoor' : 'inPerson'] : undefined),
    [isDropoffAtDoor, preferences]
  );

  const combinedOptions = useMemo(() => {
    if (!pickUpOptions?.categories && !dropOffOptions?.categories) {
      return { categories: [] };
    }

    const addServiceLineToOptions = (categories: Preferences.Category[], prefix: string) =>
      categories.map((category) => ({
        ...category,
        key: `${serviceLine}-${prefix}-${category.key}`,
        text: t(`homePage.${serviceLine}${prefix === 'pickup' ? 'Collection' : 'Delivery'}InstructionHeading`),
        options: category.options.map((option) => ({
          ...option,
          key: `${serviceLine}-${prefix}-${option.key}`,
        })),
      }));

    const pickupCategories = pickUpOptions?.categories ? addServiceLineToOptions(pickUpOptions.categories, 'pickup') : [];
    const dropoffCategories = dropOffOptions?.categories ? addServiceLineToOptions(dropOffOptions.categories, 'dropoff') : [];

    return {
      categories: showCollection ? [...pickupCategories, ...dropoffCategories] : dropoffCategories,
    };
  }, [pickUpOptions?.categories, dropOffOptions?.categories, showCollection, serviceLine, t]);

  useEffect(() => {
    const updatePreselections = () => {
      const preSelectedValues = combinedOptions?.categories.map((category) =>
        category.options.filter((option) => Boolean(option.value)).map((option) => option.key)
      );

      const flatSelections = preSelectedValues ? preSelectedValues?.reduce((accumulator, current) => accumulator.concat(current), []) : [];

      setSelectedOptions(flatSelections);
    };

    updatePreselections();
  }, [combinedOptions?.categories]);

  const updateOptions = useCallback(() => {
    if (!formRef.current) return;
    const { elements } = formRef.current;
    const options = Array.from(elements)
      .filter(
        (el) =>
          // @ts-ignore
          el.checked
      )
      .reduce((accumulator, el) => {
        const key = el.getAttribute('value') || '';
        return [...accumulator, key];
      }, [] as string[]);

    setSelectedOptions(options);
  }, [formRef]);

  const onChangeValue = useCallback(
    (type: string) => {
      updateOptions();
      Avo.orderInstructionsAdded({ instructionType: type });
    },
    [updateOptions]
  );

  return (
    <StyledOrderInstructionForm
      formRef={formRef}
      checkboxType="checkbox"
      selectedOptions={selectedOptions}
      preference={combinedOptions}
      onChangeValue={onChangeValue}
      extraKey={serviceLine}
    />
  );
};

export default DriverInstructions;
